import React, { useState, useEffect } from 'react'
import { Tag, Settings, Eye, Printer, Calendar, Clock } from 'lucide-react'
import Modal from './Modal'
import Button from './ui/Button'
import Input from './ui/Input'
import Select from './ui/Select'
import { TAMANHOS_ETIQUETA, DadosEtiqueta, ConfiguracaoEtiqueta, imprimirEtiqueta, preVisualizarEtiqueta, carregarConfiguracaoEtiqueta, salvarConfiguracaoEtiqueta } from '../utils/impressaoEtiqueta'
import { useToast } from './Toast'

interface EtiquetaModalProps {
  isOpen: boolean
  onClose: () => void
  dados: DadosEtiqueta
}

const EtiquetaModal: React.FC<EtiquetaModalProps> = ({ isOpen, onClose, dados }) => {
  const { success, error } = useToast()
  const [config, setConfig] = useState<ConfiguracaoEtiqueta>(carregarConfiguracaoEtiqueta())
  const [larguraCustom, setLarguraCustom] = useState(config.larguraCustom?.toString() || '90')
  const [alturaCustom, setAlturaCustom] = useState(config.alturaCustom?.toString() || '38')
  const [isPrinting, setIsPrinting] = useState(false)

  useEffect(() => {
    if (isOpen) {
      const savedConfig = carregarConfiguracaoEtiqueta()
      setConfig(savedConfig)
      setLarguraCustom(savedConfig.larguraCustom?.toString() || '90')
      setAlturaCustom(savedConfig.alturaCustom?.toString() || '38')
    }
  }, [isOpen])

  const handleTamanhoChange = (tamanhoId: string) => {
    const tamanho = TAMANHOS_ETIQUETA.find(t => t.id === tamanhoId) || TAMANHOS_ETIQUETA[0]
    setConfig(prev => ({ ...prev, tamanho }))
  }

  const handlePrevisualizar = () => {
    const configFinal: ConfiguracaoEtiqueta = {
      ...config,
      larguraCustom: config.tamanho.id === 'custom' ? parseFloat(larguraCustom) : undefined,
      alturaCustom: config.tamanho.id === 'custom' ? parseFloat(alturaCustom) : undefined
    }
    preVisualizarEtiqueta(dados, configFinal)
  }

  const handleImprimir = async () => {
    try {
      setIsPrinting(true)
      const configFinal: ConfiguracaoEtiqueta = {
        ...config,
        larguraCustom: config.tamanho.id === 'custom' ? parseFloat(larguraCustom) : undefined,
        alturaCustom: config.tamanho.id === 'custom' ? parseFloat(alturaCustom) : undefined
      }
      salvarConfiguracaoEtiqueta(configFinal)
      await imprimirEtiqueta(dados, configFinal)
      success('Etiqueta enviada para impressao')
    } catch (err) {
      console.error('Erro:', err)
      error('Erro ao imprimir etiqueta')
    } finally {
      setIsPrinting(false)
    }
  }

  const tamanhoOptions = TAMANHOS_ETIQUETA.map(t => ({ value: t.id, label: t.nome }))

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Imprimir Etiqueta" size="md">
      <div className="space-y-5">
        {/* Preview dos dados da etiqueta */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
          <h4 className="text-sm font-semibold text-blue-800 mb-3 flex items-center gap-2">
            <Tag className="h-4 w-4" />
            Pre-visualizacao dos Dados
          </h4>
          <div className="bg-white rounded-lg p-3 shadow-sm border border-blue-100">
            {/* Header simulado */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white text-xs font-semibold px-2 py-1 rounded-t flex justify-between items-center">
              <span className="truncate">{dados.nomeEmpresa}</span>
              <span className="bg-white text-blue-600 px-1.5 py-0.5 rounded text-[10px] font-bold">OS</span>
            </div>
            {/* Conteudo simulado */}
            <div className="p-2 space-y-1">
              <div className="text-center text-lg font-bold text-gray-900">
                <span className="text-blue-600">#</span>{dados.numeroOS}
              </div>
              <div className="text-center">
                <span className="text-[10px] text-gray-400 uppercase tracking-wider">Cliente</span>
                <div className="text-sm font-semibold text-gray-800 truncate">{dados.nomeCliente}</div>
              </div>
              {(config.mostrarEquipamento && dados.equipamento) || (config.mostrarData && dados.dataEntrada) || (config.mostrarTelefone && dados.telefone) ? (
                <div className="border-t border-dashed border-gray-200 pt-1 mt-1 space-y-0.5">
                  {config.mostrarEquipamento && dados.equipamento && (
                    <div className="text-[10px] text-gray-500 text-center">
                      <span className="text-gray-400">Equip:</span> {dados.equipamento}
                    </div>
                  )}
                  {config.mostrarData && dados.dataEntrada && (
                    <div className="text-[10px] text-gray-500 text-center flex items-center justify-center gap-1">
                      <Calendar className="h-2.5 w-2.5" />
                      {dados.dataEntrada} {dados.horaEntrada && <><Clock className="h-2.5 w-2.5 ml-1" />{dados.horaEntrada}</>}
                    </div>
                  )}
                  {config.mostrarTelefone && dados.telefone && (
                    <div className="text-[10px] text-gray-500 text-center">
                      <span className="text-gray-400">Tel:</span> {dados.telefone}
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* Configuracoes */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Configuracoes da Impressao
          </h4>

          <Select
            label="Tamanho da Etiqueta"
            value={config.tamanho.id}
            onChange={(e) => handleTamanhoChange(e.target.value)}
            options={tamanhoOptions}
          />

          {config.tamanho.id === 'custom' && (
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Largura (mm)"
                type="number"
                value={larguraCustom}
                onChange={(e) => setLarguraCustom(e.target.value)}
              />
              <Input
                label="Altura (mm)"
                type="number"
                value={alturaCustom}
                onChange={(e) => setAlturaCustom(e.target.value)}
              />
            </div>
          )}

          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
            <p className="text-xs font-medium text-gray-600 mb-2">Informacoes a incluir:</p>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={config.mostrarData}
                onChange={(e) => setConfig(prev => ({ ...prev, mostrarData: e.target.checked }))}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Data e hora de entrada</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={config.mostrarEquipamento}
                onChange={(e) => setConfig(prev => ({ ...prev, mostrarEquipamento: e.target.checked }))}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Equipamento</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={config.mostrarTelefone || false}
                onChange={(e) => setConfig(prev => ({ ...prev, mostrarTelefone: e.target.checked }))}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Telefone do cliente</span>
            </label>
          </div>
        </div>

        {/* Info do tamanho */}
        {config.tamanho.id !== 'custom' && (
          <div className="text-xs text-blue-600 bg-blue-50 rounded-lg px-3 py-2 flex items-center gap-2">
            <div className="w-4 h-3 border border-blue-400 rounded-sm"></div>
            Tamanho selecionado: {config.tamanho.largura}mm x {config.tamanho.altura}mm
          </div>
        )}

        {/* Botoes */}
        <div className="flex justify-between pt-4 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={handlePrevisualizar}
            leftIcon={<Eye className="h-4 w-4" />}
          >
            Pre-visualizar
          </Button>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              onClick={handleImprimir}
              isLoading={isPrinting}
              leftIcon={<Printer className="h-4 w-4" />}
            >
              Imprimir
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default EtiquetaModal
